import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';

interface LocalizationFormData {
  defaultLanguage: string;
  timezone: string;
  dateFormat: string;
  timeFormat: string;
}

interface LocalizationSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const languages = [
  { value: 'en', label: 'English' },
  { value: 'tr', label: 'Turkish (Türkçe)' },
  { value: 'de', label: 'German (Deutsch)' },
  { value: 'fr', label: 'French (Français)' },
  { value: 'es', label: 'Spanish (Español)' },
  { value: 'ar', label: 'Arabic (العربية)' },
];

const timezones = [
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  { value: 'Europe/Istanbul', label: 'Istanbul (GMT+3)' },
  { value: 'Europe/London', label: 'London (GMT+0/+1)' },
  { value: 'Europe/Paris', label: 'Paris (GMT+1/+2)' },
  { value: 'Europe/Berlin', label: 'Berlin (GMT+1/+2)' },
  { value: 'America/New_York', label: 'New York (GMT-5/-4)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (GMT-8/-7)' },
  { value: 'Asia/Dubai', label: 'Dubai (GMT+4)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (GMT+9)' },
];

const dateFormats = [
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (31/12/2024)' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (12/31/2024)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2024-12-31)' },
  { value: 'DD.MM.YYYY', label: 'DD.MM.YYYY (31.12.2024)' },
  { value: 'DD-MM-YYYY', label: 'DD-MM-YYYY (31-12-2024)' },
];

const timeFormats = [
  { value: '24h', label: '24-hour (14:30)' },
  { value: '12h', label: '12-hour (2:30 PM)' },
];

export function LocalizationSettingsModal({ open, onOpenChange }: LocalizationSettingsModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: settings, isLoading } = trpc.settings.getLocalizationSettings.useQuery(undefined, {
    enabled: open,
  });

  const {
    handleSubmit,
    reset,
    watch,
    setValue,
  } = useForm<LocalizationFormData>({
    defaultValues: {
      defaultLanguage: 'en',
      timezone: 'UTC',
      dateFormat: 'DD/MM/YYYY',
      timeFormat: '24h',
    },
  });

  useEffect(() => {
    if (settings) {
      reset({
        defaultLanguage: (settings['localization.defaultLanguage'] as string) || 'en',
        timezone: (settings['localization.timezone'] as string) || 'UTC',
        dateFormat: (settings['localization.dateFormat'] as string) || 'DD/MM/YYYY',
        timeFormat: (settings['localization.timeFormat'] as string) || '24h',
      });
    }
  }, [settings, reset]);

  const utils = trpc.useUtils();

  const updateMutation = trpc.settings.updateLocalizationSettings.useMutation({
    onSuccess: () => {
      utils.settings.getLocalizationSettings.invalidate();
      onOpenChange(false);
    },
    onError: () => {
      setIsSubmitting(false);
    },
  });

  const onSubmit = (data: LocalizationFormData) => {
    setIsSubmitting(true);
    updateMutation.mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Localization Settings</DialogTitle>
          <DialogDescription>
            Configure language, timezone, and date/time format preferences.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Default Language</Label>
                <Combobox
                  options={languages.map((lang) => ({
                    value: lang.value,
                    label: lang.label,
                  }))}
                  value={watch('defaultLanguage')}
                  onValueChange={(value) => setValue('defaultLanguage', value)}
                  placeholder="Select language"
                  searchPlaceholder="Search languages..."
                />
              </div>

              <div className="space-y-2">
                <Label>Timezone</Label>
                <Combobox
                  options={timezones.map((tz) => ({
                    value: tz.value,
                    label: tz.label,
                  }))}
                  value={watch('timezone')}
                  onValueChange={(value) => setValue('timezone', value)}
                  placeholder="Select timezone"
                  searchPlaceholder="Search timezones..."
                />
              </div>

              <div className="space-y-2">
                <Label>Date Format</Label>
                <Combobox
                  options={dateFormats.map((format) => ({
                    value: format.value,
                    label: format.label,
                  }))}
                  value={watch('dateFormat')}
                  onValueChange={(value) => setValue('dateFormat', value)}
                  placeholder="Select date format"
                  searchPlaceholder="Search..."
                />
              </div>

              <div className="space-y-2">
                <Label>Time Format</Label>
                <Combobox
                  options={timeFormats.map((format) => ({
                    value: format.value,
                    label: format.label,
                  }))}
                  value={watch('timeFormat')}
                  onValueChange={(value) => setValue('timeFormat', value)}
                  placeholder="Select time format"
                  searchPlaceholder="Search..."
                />
              </div>
            </div>

            {updateMutation.error && (
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                {updateMutation.error.message}
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
